import React, { FC } from 'react';
import { StoreContact, SiteOpenTime } from 'core/sites/types';
import { RetailSiteInfoProps } from '../../types';
import SiteImages from '../SiteImages';
import './retailSiteInfo.scss';

const formatTime = (time: string | null | undefined): string => {
    if (!time || typeof time !== 'string') return '-';
    const parts = time.split(':');
    let hours = parseInt(parts[0] ?? '0', 10);
    const minutes = parts[1] ?? '00';
    const period = hours >= 12 ? 'PM' : 'AM';
    if (hours === 0) hours = 12;
    else if (hours > 12) hours -= 12;
    return `${hours}:${minutes} ${period}`;
};

const isValidContact = (contact: unknown): contact is StoreContact => (
    contact != null && typeof contact === 'object' && 'id' in contact && typeof (contact as StoreContact).id === 'number'
);

const isValidOpenTime = (openTime: unknown): openTime is SiteOpenTime => (
    openTime != null && typeof openTime === 'object' && 'id' in openTime && typeof (openTime as SiteOpenTime).id === 'number'
);

const StoreContactsTable: FC<{ contacts: StoreContact[] }> = ({ contacts }) => {
    const validContacts = contacts.filter(isValidContact);
    if (validContacts.length === 0) return <div className="retail-info-value">-</div>;
    return (
        <table className="store-contacts-table">
            <tbody>
                {validContacts.map((contact) => (
                    <tr key={contact.id}>
                        <td>{contact.name || '-'}</td>
                        <td>{contact.role || '-'}</td>
                        <td>{contact.email || '-'}</td>
                        <td>{contact.mobileNumber || '-'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const OpenTimesList: FC<{ openTimes: SiteOpenTime[] }> = ({ openTimes }) => {
    const validTimes = openTimes.filter(isValidOpenTime);
    if (validTimes.length === 0) return <div className="retail-info-value">-</div>;
    return (
        <div className="open-times-list">
            {validTimes.map((openTime) => (
                <div className="open-times-row" key={openTime.id}>
                    <span className="open-times-day">{openTime.day || '-'}:</span>
                    <span className="open-times-hours">
                        {formatTime(openTime.startTime)} - {formatTime(openTime.endTime)}
                    </span>
                </div>
            ))}
        </div>
    );
};

const RetailSiteInfo: FC<RetailSiteInfoProps> = ({ siteDetail, isAdmin }) => {
    const { siteInfo: retailSiteInfo } = siteDetail;
    const hasContacts = Array.isArray(siteDetail.storeContacts) && siteDetail.storeContacts.length > 0;
    const hasOpenTimes = Array.isArray(siteDetail.openTimes) && siteDetail.openTimes.length > 0;

    if (isAdmin) {
        return (
            <>
                <div className="site-info-card">
                    <div className="retail-info-section retail-info-section--four-col">
                        <div className="retail-info-column">
                            <div className="retail-info-label">Store Code:</div>
                            <div className="retail-info-value">{retailSiteInfo?.storeCode || '-'}</div>
                        </div>
                        <div className="retail-info-column">
                            <div className="retail-info-label">Static Code:</div>
                            <div className="retail-info-value">{siteDetail.staticSiteCode || '-'}</div>
                        </div>
                        <div className="retail-info-column">
                            <div className="retail-info-label">Response Code:</div>
                            <div className="retail-info-value">{retailSiteInfo?.mitieCodeResponse || '-'}</div>
                        </div>
                        <div className="retail-info-column">
                            <div className="retail-info-label">What3Words:</div>
                            <div className="retail-info-value">
                                {retailSiteInfo?.what3Words ? (
                                    <a
                                        href={`https://what3words.com/${retailSiteInfo.what3Words}`}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {'///'}{retailSiteInfo.what3Words}
                                    </a>
                                ) : '-'}
                            </div>
                        </div>
                        <div className="retail-info-column">
                            <div className="retail-info-label">Incident Risk Level:</div>
                            <div className="retail-info-value">{retailSiteInfo?.incidentRiskLevelLabel || '-'}</div>
                        </div>
                        <div className="retail-info-column">
                            <div className="retail-info-label">No Access Out of Hours:</div>
                            <div className="retail-info-value">{siteDetail.noAccessOutOfHours ? 'Yes' : 'No'}</div>
                        </div>
                    </div>

                    <div className="store-contacts-section">
                        <div className="store-contacts-heading">Store Contacts:</div>
                        {hasContacts ? (
                            <StoreContactsTable contacts={siteDetail.storeContacts ?? []} />
                        ) : (
                            <div className="retail-info-value">-</div>
                        )}
                    </div>

                    <div className="region-times-section">
                        <div className="times-column">
                            <div className="retail-info-label">Opening Times:</div>
                            {hasOpenTimes ? (
                                <OpenTimesList openTimes={siteDetail.openTimes ?? []} />
                            ) : (
                                <div className="retail-info-value">-</div>
                            )}
                        </div>
                    </div>
                </div>
                <SiteImages
                    encryptedId={siteDetail.encryptedId}
                    siteImages={siteDetail.siteImages || []} />
            </>
        );
    }

    return (
        <div className="site-info-card">
            <div className="retail-info-section">
                <div className="retail-info-column">
                    <div className="retail-info-label">Store Code:</div>
                    <div className="retail-info-value">{retailSiteInfo?.storeCode || '-'}</div>
                </div>
                <div className="retail-info-column">
                    <div className="retail-info-label">What3Words:</div>
                    <div className="retail-info-value">
                        {retailSiteInfo?.what3Words ? (
                            <a
                                href={`https://what3words.com/${retailSiteInfo.what3Words}`}
                                target="_blank"
                                rel="noopener noreferrer">
                                {'///'}{retailSiteInfo.what3Words}
                            </a>
                        ) : '-'}
                    </div>
                </div>
                <div className="retail-info-column">
                    <div className="retail-info-label">Opening Times:</div>
                    {hasOpenTimes ? (
                        <OpenTimesList openTimes={siteDetail.openTimes ?? []} />
                    ) : (
                        <div className="retail-info-value">-</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RetailSiteInfo;
